import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Chip, Icon, Pane, PaneHead } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { errorText, useReceivers, useTechActions, useWorklist, type Receiver } from './api';
import { drName, mmss, secondsUntil, shortName, useNow } from './format';
import { buildItems, type Item } from './model';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

/** What can move: an open test nobody else holds — not one in progress, not a
    sent one, not a test-only walk-in (the patient is standing at the desk). */
const movable = (i: Item) =>
  i.tasks.some((t) => t.state === 'open') && !i.next.testOnly && i.lane !== 'with_you' && i.lane !== 'done';

function stateLine(i: Item): string {
  const t = i.next;
  if (i.bundle) {
    const done = i.tasks.filter((x) => x.state === 'done').length;
    return `${done} of ${i.tasks.length} done · order matters`;
  }
  if (i.lane === 'waiting') return 'hand it over and the clock keeps running';
  return [t.priority !== 'routine' ? t.priority : '', 'not started'].filter(Boolean).join(' · ');
}

const initials = (r: Receiver) => `${r.firstName[0] ?? ''}${r.lastName[0] ?? ''}`.toUpperCase();

/**
 * Technician · Hand over — `g_hand`, phone `g_t6`. The set, then the person.
 *
 * Pick mode: the table gains tick cells and the pane becomes the SELECTION SET.
 * The rows that cannot move are not drawn. A claimed test releases its claim
 * as it moves; a dilation clock keeps running.
 */
export function TechHandOverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const preset = (location.state as { select?: string[] } | null)?.select ?? null;
  const list = useWorklist();
  const people = useReceivers();
  const act = useTechActions();
  const items = useMemo(() => buildItems(list.data?.tasks ?? []).filter(movable), [list.data]);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [to, setTo] = useState<string | null>(null);
  usePageMeta({ title: 'Hand over' });
  const ov = useTechOverlays();
  const now = useNow(items.some((i) => i.next.waiting?.until));

  /* What can start now starts ticked; a test on a running clock stays with you
     until you tick it. Handed over from its own capture, only that one is. */
  const ticked = picked ?? new Set(
    items.filter((i) => (preset ? i.tasks.some((t) => preset.includes(t.id)) : i.lane !== 'waiting')).map((i) => i.id),
  );
  const toggle = (id: string) => {
    const next = new Set(ticked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const receivers = people.data ?? [];
  const receiver = receivers.find((r) => r.id === to) ?? null;
  const chosen = items.filter((i) => ticked.has(i.id));
  const taskIds = chosen.flatMap((i) => i.tasks.filter((t) => t.state === 'open').map((t) => t.id));
  const submit = () => {
    if (!receiver) {
      toast.error('Pick who they go to');
      return;
    }
    if (!taskIds.length) {
      toast.error('Tick what moves');
      return;
    }
    act.handOver.mutate({ toUserId: receiver.id, taskIds }, {
      onSuccess: () => {
        toast.success(`${chosen.length} handed to ${shortName(receiver)}`);
        navigate('/technician/worklist');
      },
      onError: (e) => toast.error(errorText(e)),
    });
  };

  const sig = `${items.map((i) => `${i.id}:${ticked.has(i.id)}`).join(',')}|${to}|${receivers.length}`;

  useShellChrome(
    {
      raw: true,
      title: 'Hand over',
      sub: (
        <>
          <button type="button" className="lnk" style={{ fontWeight: 600 }} onClick={() => navigate('/technician/worklist')}>‹ Worklist</button>
          {' ▸ Hand over'}
        </>
      ),
      search: 'Search orders, patients, MRN…',
      onSearch: ov.openSearch,
      onBell: ov.openBell,
      primary: { label: 'Hand over', onClick: submit },
      pane: (
        <Pane
          head={<PaneHead overline="Selection set" title="What moves" sub="tick a row — this panel is the set" />}
          foot={<button type="button" className="btn btn-outline grow" onClick={() => navigate('/technician/worklist')}>Cancel</button>}
        >
          {items.map((i) => {
            const on = ticked.has(i.id);
            const label = i.bundle ? `${shortName(i.next.patient)} — bundle of ${i.tasks.length}` : `${i.next.title} · ${shortName(i.next.patient)}`;
            return (
              <button key={i.id} type="button" className={`row w-full text-start${on ? ' row-on' : ''}`}
                style={on ? { marginBottom: 8 } : { marginBottom: 8, background: 'transparent', borderStyle: 'dashed' }}
                onClick={() => toggle(i.id)}>
                {on ? <span className="grn w6">✓</span> : <span className="mut">—</span>}
                <div className="grow">
                  <p className={`t-sec${on ? ' w6' : ' mut'}`}>{label}</p>
                  <p className="t-sec mut">{on ? stateLine(i) : 'stays with you'}</p>
                </div>
              </button>
            );
          })}
          {items.length === 0 ? <div className="emptystate"><p className="t-sec">Nothing on your list can move right now.</p></div> : null}
          <p className="t-ovl mut mt16 mb8">Hand them to</p>
          {receivers.map((r) => (
            <button key={r.id} type="button" className={`row w-full text-start${to === r.id ? ' row-on' : ''}`} style={{ marginBottom: 8 }}
              onClick={() => setTo(r.id)}>
              <span className="av av-sm">{initials(r)}</span>
              <div className="grow">
                <p className="t-sec w6">{r.firstName} {r.lastName}</p>
                <p className="t-sec mut">{r.role}{r.presentToday ? ' · here today' : ' · not signed in today'}</p>
              </div>
              {to === r.id ? <span className="brand"><Icon name="check" /></span> : null}
            </button>
          ))}
          {people.isLoading ? <p className="t-sec mut" role="status">Finding who is on shift…</p> : null}
          {people.data && receivers.length === 0 ? (
            <div className="card card-ghost"><p className="t-sec mut">Nobody else holds the Technician role here yet.</p></div>
          ) : null}
          <div className="card card-acc mt12">
            <p className="t-sec" style={{ color: 'var(--brand-600)' }}>
              The tests land on {receiver ? `${receiver.firstName}’s` : 'their'} worklist unchanged. A claimed task releases its claim as it moves;
              the dilation clock keeps running.
            </p>
          </div>
        </Pane>
      ),
      phone: <PhoneHandOver items={items} ticked={ticked} toggle={toggle} receivers={receivers} to={to} setTo={setTo} submit={submit}
        all={() => setPicked(new Set(items.map((i) => i.id)))} busy={act.handOver.isPending} />,
    },
    [sig, act.handOver.isPending],
  );

  return (
    <div className="maie desk tech-root">
        {ov.overlays}
      <div className="dcontent">
        <table className="dtable">
          <thead>
            <tr>
              <th style={{ width: '5%' }} />
              <th style={{ width: '42%' }}>Task</th>
              <th style={{ width: '21%' }}>Patient</th>
              <th style={{ width: '11%' }}>Takes</th>
              <th style={{ width: '21%' }}>State</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => {
              const on = ticked.has(i.id);
              const t = i.next;
              const minutes = i.tasks.reduce((s, x) => s + (x.takesMin ?? 0), 0);
              const left = secondsUntil(t.waiting?.until, now);
              return (
                <tr key={i.id} className={`rowc${on ? ' sel' : ''}`} onClick={() => toggle(i.id)}>
                  <td>
                    <span className="tech-tick" role="checkbox" aria-checked={on} aria-label={`Tick ${t.title}`}>{on ? '✓' : ''}</span>
                  </td>
                  <td>
                    <p className="t-body w6">{i.bundle ? `${shortName(t.patient)} — bundle of ${i.tasks.length}` : t.title}</p>
                    <p className="t-cap mut">{stateLine(i)}</p>
                  </td>
                  <td><span className="t-sec sec">{[shortName(t.patient), drName(t.requestedBy)].filter(Boolean).join(' · ')}</span></td>
                  <td><span className="t-sec sec" style={{ whiteSpace: 'nowrap' }}>{minutes ? `${minutes} min` : '—'}</span></td>
                  <td>
                    {i.lane === 'waiting' ? (
                      <Chip kind="warn" small><Icon name="clock" size="sm" /> {t.waiting?.label}{left != null ? ` ${mmss(left)}` : ''}</Chip>
                    ) : i.bundle ? <Chip kind="soft" small>one sitting</Chip>
                      : t.priority !== 'routine' ? <Chip kind="danger" small>{t.priority}</Chip>
                        : <Chip kind="outline" small>not started</Chip>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {items.length === 0 && list.data ? (
          <div className="emptystate mt12"><p className="es-t">Nothing on your list can move right now.</p></div>
        ) : null}
      </div>
    </div>
  );
}

/* ─────────────────────────── phone · g_t6 ─────────────────────────── */

function PhoneHandOver({ items, ticked, toggle, receivers, to, setTo, submit, all, busy }: {
  items: Item[];
  ticked: Set<string>;
  toggle: (id: string) => void;
  receivers: Receiver[];
  to: string | null;
  setTo: (id: string) => void;
  submit: () => void;
  all: () => void;
  busy: boolean;
}) {
  const navigate = useNavigate();
  const n = items.filter((i) => ticked.has(i.id)).length;
  return (
    <PhoneScreen door="worklist" crumb="Worklist" title="Hand over" onBack={() => navigate('/technician/worklist')}>
      <div className="flex mb12" style={{ gap: 8 }}>
        <Chip kind="soft" small className="w6" onClick={all}>All</Chip>
        <p className="t-sec mut grow">{n} of {items.length} selected</p>
      </div>
      {items.map((i) => {
        const on = ticked.has(i.id);
        const t = i.next;
        return (
          <button key={i.id} type="button" className="card card-btn" style={on ? { borderColor: 'var(--brand-500)' } : undefined}
            onClick={() => toggle(i.id)} aria-pressed={on}>
            <div className="flex" style={{ gap: 10 }}>
              <span className="tech-tick" aria-checked={on}>{on ? '✓' : ''}</span>
              <div className="grow">
                <p className="t-strong">{i.bundle ? `${shortName(t.patient)} — bundle of ${i.tasks.length}` : `${t.title} · ${shortName(t.patient)}`}</p>
                <p className="t-sec mut">{i.lane === 'waiting' ? `⏱ ${t.waiting?.label} · ` : ''}{stateLine(i)}</p>
              </div>
            </div>
          </button>
        );
      })}
      <div className="card card-acc mt16">
        <p className="t-sec w6 mb8" style={{ color: 'var(--brand-600)' }}>Hand {n} task{n === 1 ? '' : 's'} to</p>
        {receivers.map((r, idx) => (
          <button key={r.id} type="button" className="flex w-full text-start" onClick={() => setTo(r.id)}
            style={{ gap: 10, padding: '8px 0', borderBottom: idx < receivers.length - 1 ? '1px solid var(--stroke-200)' : 'none', background: 'none', borderLeft: 'none', borderRight: 'none', borderTop: 'none', cursor: 'pointer', font: 'inherit', color: 'inherit' }}>
            <span className="av av-sm">{initials(r)}</span>
            <p className={`t-strong grow${to === r.id ? ' brand' : ''}`}>{shortName(r)}</p>
            <p className="t-cap mut">{r.role}{r.presentToday ? ' · here today' : ' · not signed in today'}{to === r.id ? ' ✓' : ''}</p>
          </button>
        ))}
        {receivers.length === 0 ? <p className="t-sec mut">Nobody else holds the Technician role here yet.</p> : null}
      </div>
      <button type="button" className="btn btn-primary mt12" disabled={!to || !n || busy} onClick={submit}>Hand over</button>
    </PhoneScreen>
  );
}
